/**
 * Da quale linea è entrata la chiamata.
 *
 * Il centralino non lo dice nel numero, lo dice nel *nome* del chiamante. Ogni
 * Inbound Route ha un campo "CID Name Prefix" che FreePBX antepone a
 * `CALLERID(name)`: sulle quattro route CEFIN vale `CEFIN0: ` … `CEFIN3: `, una
 * per DID, così l'operatore sa quale dei numeri il cliente ha composto. Il
 * prefisso viaggia nel display-name dell'header `From`, che SIP.js espone come
 * `invitation.remoteIdentity.displayName`.
 *
 * Il display-name però non contiene solo il prefisso: per le chiamate esterne
 * il CNAM è quasi sempre il numero stesso, quindi arriva roba come
 * `CEFIN0:+393401234567` (verificato nel CDR del 2026-08-03). Mostrarlo intero
 * accanto al numero significherebbe scrivere due volte la stessa cifra, con il
 * pezzo che serve davvero nascosto in mezzo.
 *
 * Questa funzione tiene solo la parte che aggiunge informazione.
 */

/** Cifre nude, per confrontare numeri scritti in formati diversi. */
function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Quante cifre finali devono coincidere perché due scritture siano lo stesso
 * numero. Il display-name e l'URI non arrivano dallo stesso posto: il primo è
 * il CNAM del provider, il secondo il numero che il centralino ha normalizzato,
 * quindi `340 123 4567` e `+393401234567` sono la stessa persona. Sette cifre
 * sono abbastanza da non far collassare due chiamanti diversi.
 */
const MIN_MATCHING_DIGITS = 7;

function isSameNumber(a: string, b: string): boolean {
  if (!a || !b) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length < MIN_MATCHING_DIGITS) return shorter === longer;
  return longer.endsWith(shorter);
}

/**
 * Estrae dal display-name la parte che non è il numero del chiamante.
 *
 * Con le route CEFIN restituisce il prefisso di linea (`CEFIN0`); con un
 * centralino che manda un vero CNAM restituisce il nome del chiamante. In
 * entrambi i casi è qualcosa che l'operatore non ha già davanti, quindi vale la
 * pena mostrarlo.
 *
 * @param displayName display-name SIP, come arriva da `remoteIdentity`
 * @param phoneNumber numero del chiamante già estratto dall'URI
 * @returns l'etichetta da mostrare, o `null` se non aggiunge niente al numero
 */
export function extractCallerLine(
  displayName: string | null | undefined,
  phoneNumber: string | null | undefined
): string | null {
  if (!displayName) return null;

  const name = displayName.trim();
  if (!name) return null;

  let label = name;

  const callerDigits = onlyDigits(phoneNumber ?? "");
  if (callerDigits) {
    // Coda numerica del display-name: la si toglie solo se è davvero il
    // chiamante, non un numero qualsiasi finito dentro al nome.
    const tail = name.match(/[+\d][\d\s().+-]*$/);
    if (tail && isSameNumber(onlyDigits(tail[0]), callerDigits)) {
      label = name.slice(0, name.length - tail[0].length);
    }
  }

  // Il prefisso porta con sé il proprio separatore (`CEFIN0: `, `Clienti - `):
  // senza questa ripulitura finirebbe a schermo con la punteggiatura penzoloni.
  label = label.replace(/[\s:;,\-|]+$/, "").trim();

  return label || null;
}
